import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, ChevronRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import Loader from '@/components/common/Loader';
import ProductCard from '@/components/common/ProductCard';
import { useStore } from '@/context/StoreContext';
import { inr } from '@/lib/format';

export default function BundleDetail() {
  const { slug } = useParams();
  const [bundle, setBundle] = useState(null);
  const [adding, setAdding] = useState(false);
  const { addToCart } = useStore();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    setBundle(null);
    api.get(`/bundles/${slug}`).then((r) => setBundle(r.data)).catch(() => setBundle(false));
  }, [slug]);

  if (bundle === null) return <Loader />;
  if (bundle === false) return <div className="container-gaze py-20 text-center">Bundle not found</div>;

  const addAll = () => {
    setAdding(true);
    const items = (bundle.products || []).filter((p) => p.in_stock);
    if (items.length === 0) { toast.error('These pieces are currently out of stock'); setAdding(false); return; }
    items.forEach((p) => addToCart(p, 1));
    toast.success(`Added ${items.length} pieces from ${bundle.name}`);
    setTimeout(() => setAdding(false), 500);
  };

  const buyNow = () => {
    addAll();
    setTimeout(() => navigate('/checkout'), 800);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="bundle-hero">
        <div className="absolute inset-0">
          {bundle.hero_image && <img src={bundle.hero_image} alt={bundle.name} className="h-full w-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/20" />
        </div>
        <div className="container-gaze relative flex min-h-[46vh] flex-col justify-center py-16 text-white">
          <nav className="mb-2 flex items-center gap-1 text-xs text-white/80"><Link to="/bundles">Bundles</Link><ChevronRight size={12} /><span>{bundle.name}</span></nav>
          <p className="eyebrow mb-2 !text-[hsl(var(--gaze-gold))]">Complete Set</p>
          <h1 className="font-display text-4xl sm:text-6xl">{bundle.name}</h1>
          <p className="mt-3 max-w-xl text-white/85">{bundle.description}</p>
        </div>
      </section>

      {/* Sticky bundle summary */}
      <section className="container-gaze -mt-12 mb-8">
        <div className="grid gap-6 rounded-3xl border border-[hsl(var(--gaze-gold))]/40 bg-white p-6 shadow-md md:grid-cols-2" data-testid="bundle-summary">
          <div>
            <p className="eyebrow !text-[hsl(var(--gaze-gold))]">In this bundle</p>
            <h2 className="font-display text-2xl">{bundle.product_count} curated pieces</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {bundle.products.slice(0, 6).map((p) => (
                <li key={p.id} className="flex items-start gap-2"><Check size={14} className="mt-1 shrink-0 text-[hsl(var(--gaze-gold))]" /> <span className="flex-1">{p.name}</span><span className="text-xs text-muted-foreground">{inr(p.price)}</span></li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col justify-between">
            <div>
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-bold">{inr(bundle.bundle_price)}</span>
                {bundle.discount > 0 && <span className="text-sm text-muted-foreground line-through">{inr(bundle.subtotal)}</span>}
                {bundle.discount > 0 && <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5 text-xs font-semibold text-[hsl(var(--primary))]">Save {inr(bundle.discount)}</span>}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Bundle price includes an editorial discount when you buy the entire look.</p>
              <p className="mt-3 text-sm">{bundle.in_stock ? <span className="font-semibold text-emerald-700">In stock — {bundle.stock} sets available</span> : <span className="font-semibold text-red-700">Currently unavailable</span>}</p>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={addAll} disabled={adding || !bundle.in_stock} data-testid="bundle-add-all" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-6 py-3 text-sm font-semibold text-white disabled:opacity-60"><ShoppingBag size={16} /> {adding ? 'Adding…' : 'Add Entire Bundle to Cart'}</button>
              <button onClick={buyNow} disabled={adding || !bundle.in_stock} className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--gaze-gold))] bg-white px-6 py-3 text-sm font-semibold text-[hsl(var(--primary))] disabled:opacity-60">Buy the look now</button>
            </div>
          </div>
        </div>
      </section>

      {/* Products grid */}
      <section className="container-gaze py-6">
        <h2 className="mb-5 text-2xl font-semibold">Pieces in this bundle</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {bundle.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>
    </div>
  );
}
